import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CaretDown, House, Plus, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * BulkMoveBar — appears at the top of the Assets ledger whenever one or
 * more rows are selected. Lets an editor+ pick a target room (or type a
 * new one) and reassign every selected asset in a single request.
 *
 * Design notes
 * ------------
 * • Uses the same room vocabulary as the RoomPicker so the picker on
 *   the form and this bar stay in sync — new rooms typed here POST to
 *   /households/{id}/rooms first, then the assets move.
 * • Personal ledger (no household) shows the bar but the room field
 *   accepts anything (free-form) — same behaviour as RoomPicker's
 *   fallback branch.
 * • Sub-locations are intentionally left untouched on the moved assets
 *   (matches the backend contract; documented on the endpoint).
 */
export function BulkMoveBar({
  selectedIds,
  onClear,
  onMoved,
  householdId,
}) {
  const [rooms, setRooms] = useState([]);
  const [target, setTarget] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!householdId) { setRooms([]); return; }
    let cancelled = false;
    api.get(`/households/${householdId}/rooms`)
      .then((r) => { if (!cancelled) setRooms(r.data?.rooms || []); })
      .catch(() => { if (!cancelled) setRooms([]); });
    return () => { cancelled = true; };
  }, [householdId]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const qLower = (target || "").trim().toLowerCase();
  const filtered = rooms.filter((r) => !qLower || r.toLowerCase().includes(qLower));
  const exactMatch = rooms.some((r) => r.toLowerCase() === qLower);
  const showAddRow = !!qLower && !exactMatch && !!householdId;

  const commit = async (roomName) => {
    const room = (roomName ?? target).trim();
    if (!room) return;
    setSaving(true);
    try {
      // Persist a new room to the household first so the picker stays
      // consistent — otherwise the user would move assets into a room
      // that doesn't appear in the RoomPicker's dropdown.
      if (householdId && !rooms.some((r) => r.toLowerCase() === room.toLowerCase())) {
        try {
          const r = await api.post(`/households/${householdId}/rooms`, { name: room });
          setRooms(r.data?.rooms || []);
        } catch {
          // Non-fatal — the asset move still succeeds because
          // `location` is free-form on the backend.
        }
      }
      const r = await api.post("/assets/bulk/move", {
        asset_ids: selectedIds,
        location: room,
      });
      const n = r.data?.updated ?? 0;
      toast.success(
        n === selectedIds.length
          ? `Moved ${n} asset${n === 1 ? "" : "s"} to "${room}".`
          : `Moved ${n} of ${selectedIds.length} — the rest were skipped (not in this ledger).`,
      );
      setTarget("");
      setOpen(false);
      onMoved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk move failed.");
    } finally {
      setSaving(false);
    }
  };

  const count = selectedIds.length;
  if (count === 0) return null;

  return (
    <div
      ref={ref}
      data-testid="bulk-move-bar"
      className="sticky top-0 z-30 border border-[#1A3622] bg-[#F9F8F6] shadow-sm flex items-center gap-3 px-4 py-2 mb-4"
    >
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
          Selected
        </span>
        <span
          className="font-mono-ledger text-sm text-[#1A3622] tabular-nums font-bold"
          data-testid="bulk-move-count"
        >
          {String(count).padStart(2, "0")}
        </span>
      </div>

      <div className="h-4 w-px bg-[#E0DDD6]" />

      <span className="text-xs text-[#5C5F58] shrink-0 hidden sm:inline">Move to room:</span>

      <div className="relative flex-1 max-w-md">
        <input
          type="text"
          value={target}
          onChange={(e) => { setTarget(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder={householdId ? "Select or type a room…" : "e.g. Garage"}
          disabled={saving}
          data-testid="bulk-move-input"
          className="w-full bg-white border border-[#E0DDD6] px-3 py-1.5 pr-8 text-sm focus:outline-none focus:border-[#1A3622] disabled:opacity-60"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setOpen((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C5F58] hover:text-[#0A192F]"
          aria-label="Toggle room picker"
        >
          <CaretDown size={12} />
        </button>

        {open && rooms.length + (showAddRow ? 1 : 0) > 0 && (
          <div
            className="absolute left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto bg-white border border-[#1A3622] shadow-sm z-40"
            data-testid="bulk-move-menu"
          >
            {filtered.map((r) => (
              <button
                type="button"
                key={r}
                onClick={() => { setTarget(r); commit(r); }}
                data-testid={`bulk-move-option-${r}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9F8F6] flex items-center gap-2"
              >
                <House size={12} weight="regular" className="text-[#1A3622] shrink-0" />
                <span className="flex-1 text-[#0A192F]">{r}</span>
              </button>
            ))}
            {showAddRow && (
              <button
                type="button"
                onClick={() => commit(target)}
                data-testid="bulk-move-add-new"
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9F8F6] flex items-center gap-2 border-t border-[#E0DDD6] bg-[#F9F8F6]/60"
              >
                <Plus size={12} weight="bold" className="text-[#1A3622] shrink-0" />
                <span className="flex-1 text-[#0A192F]">
                  Add <span className="font-medium">&ldquo;{target.trim()}&rdquo;</span> and move
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => commit()}
        disabled={saving || !target.trim()}
        data-testid="bulk-move-submit"
        className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-1.5 text-xs font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
      >
        {saving ? "Moving…" : "Move"}
      </button>

      <button
        type="button"
        onClick={onClear}
        disabled={saving}
        data-testid="bulk-move-cancel"
        aria-label="Clear selection"
        className="p-1.5 text-[#5C5F58] hover:text-[#0A192F] hover:bg-white border border-transparent hover:border-[#E0DDD6] shrink-0 disabled:opacity-50"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}

export default BulkMoveBar;
